import React, { Component } from 'react';
import { Space, Row, Col, Badge, Tag, Typography, Image, message, Table } from 'antd';
import { AppstoreOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  ModalItem,
  InputItem,
  ButtonItem,
  dineroDisabledMask,
} from '../../../Components/Items';
import debounce from "lodash.debounce";

const { Text } = Typography;

class CatalogoModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: '',
      products: [],
      totalProducts: 0,
      loading: false,
      currentPage: 1,
      pageSize: 10
    };
  
    // Aquí defines la función debounced
    this.debouncedConsultar = debounce(() => {
      this.consultarInsumos();
    }, 500); // Puedes ajustar el delay en ms (ej. 500)
  }

  // Consultar insumos cuando se abre el modal
  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.consultarInsumos();
    }
  }

  // Función para obtener el precio correcto basado en precioId
  obtenerPrecioPorId = (insumo) => {
    // Si no hay precioId en props, usar el primer precio o el costo
    if (!this.props.precioId) {
      return insumo.precios && insumo.precios.length > 0 
        ? insumo.precios[0].precio 
        : insumo.costo;
    }

    // Buscar el precio que coincida con el precioId
    const precioEncontrado = insumo.precios && insumo.precios.find(
      precio => precio.precioId === this.props.precioId
    );

    // Si encuentra el precio, devolverlo; si no, usar el costo como fallback
    return precioEncontrado ? precioEncontrado.precio : insumo.costo;
  };

  // Consultar insumos desde la API
  consultarInsumos = () => {
    this.setState({ loading: true });
  
    const { searchTerm, currentPage, pageSize } = this.state;
  
    axios
      .post(
        "puntoVenta/selectInsumosVentaPaginado",
        {
          empresaId: this.props.empresaId,
          sesionId: this.props.sesionId,
          proyectoId: this.props.proyectoId,
          query: searchTerm,
          skip: (currentPage - 1) * pageSize,
          limit: pageSize
        },
        {
          headers: this.props.headersToken,
        }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ 
            products: res.data.items.map(insumo => ({
              id: insumo.codigo,
              name: insumo.descripcion,
              price: this.obtenerPrecioPorId(insumo), // Usar la función para obtener el precio correcto
              category: insumo.categoria || 'Sin categoría',
              stock: insumo.cantidad || 0,
              barcode: insumo.codigoBarras || 'N/A',
              archivosPrincipal: insumo.archivosPrincipal || null,
              insumoOriginal: insumo,
              inventariable: insumo.inventariable || false,
            })), 
            totalProducts: res.data.total, 
            loading: false 
          });
        } else {
          message.error("No se encontraron insumos");
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        console.error("Error al consultar insumos:", error);
        message.error("Error al consultar insumos");
        this.setState({ loading: false });
      });
      
  };

  onSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 }, () => {
      this.debouncedConsultar();
    });
  };

  onTableChange = (pagination) => {
    this.setState(
      {
        currentPage: pagination.current,
        pageSize: pagination.pageSize
      },
      () => {
        this.consultarInsumos();
      }
    );
  };
  
  // Limpiar filtros al cerrar el modal
  handleCancel = () => {
    this.setState({ 
      searchTerm: '', 
      products: [],
      currentPage: 1
    });
    this.props.onCancel();
  };

  // Agregar producto al carrito
  handleAddToCart = (product) => {
    this.props.onAddToCart(product);
  };

  // Columnas de la tabla de productos
  getProductColumns = () => [
    {
      title: 'Imagen',
      dataIndex: 'archivosPrincipal',
      key: 'imagen',
      width: 80,
      render: (text, record) => (
        <Image
          src={
            record.archivosPrincipal?.[0]
              ? "../" + record.archivosPrincipal[0]
              : "/Resources/logoBiwoInsumos.png"
          }
          alt={"Sin imagen"}
          width={50}
          style={{ borderRadius: '8px' }}
        />
      ),
    },
    {
      title: 'Producto',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Código: {record.barcode}
          </Text>
        </div>
      )
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      render: (category) => <Tag color="blue">{category}</Tag>
    },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      render: (price) => (
        <Text strong style={{ color: '#52c41a' }}>
          {dineroDisabledMask(price)}
        </Text>
      )
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      render: (stock, record) => record.inventariable ? (
        <Badge 
          count={stock || 0}
          showZero
          style={{ 
            backgroundColor: stock > 10 ? '#52c41a' : stock > 0 ? '#faad14' : '#f5222d' 
          }}
        />
      ) : null
    },
    {
      title: 'Acción',
      key: 'action',
      render: (_, record) => (
        <ButtonItem
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => this.handleAddToCart(record)}
          disabled={(!record.stock || record.stock === 0) && record.inventariable}
          style={{ 
            backgroundColor: '#00b5e2', 
            borderColor: '#00b5e2' 
          }}
          lessTime
        >
          Agregar
        </ButtonItem>
      )
    }
  ];

  render() {
    const { visible } = this.props;
    const { loading, products, currentPage, pageSize, totalProducts } = this.state;

    return (
      <ModalItem
        title={
          <Space>
            <AppstoreOutlined style={{ color: '#00b5e2' }} />
            <span>Catálogo de Productos</span>
          </Space>
        }
        visible={visible}
        onCancel={this.handleCancel}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        destroyOnClose={true}
        spinning={loading}
      >
        {/* Filtros */}
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <InputItem
              prefix={<SearchOutlined />}
              placeholder="Buscar producto o código de barras..."
              value={this.state.searchTerm}
              onChange={this.onSearchChange}
              size="large"
              allowClear
              autoFocus
            />
          </Col>
        </Row>

        {/* Información de resultados */}
        <div style={{ marginBottom: '16px' }}>
          <Text style={{ fontSize: '14px', color: '#666' }}>
            Mostrando {products.length} de {totalProducts} productos 
          </Text>
        </div>

        {/* Tabla de productos */}
        <Table
          dataSource={products}
          columns={this.getProductColumns()}
          spinning={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalProducts,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} de ${total} productos`,
            pageSizeOptions: ['10', '20', '50'],
            showLessItems: true,
            hideOnSinglePage: false,
            simple: false
          }}
          onChange={this.onTableChange}
          scroll={{ y: 400 }}
          locale={{
            emptyText: loading ? 'Cargando productos...' : 'No se encontraron productos'
          }}
        />
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
  };
};

export default connect(mapStateToProps)(CatalogoModal);